import type React from 'react';
import { createContext, memo, useCallback, useContext, useEffect, useMemo } from 'react';
import { ReplaySubject, asyncScheduler, combineLatest, merge } from 'rxjs';
import { map, scan, shareReplay, throttleTime } from 'rxjs/operators';

import {
  ExecTypeEnum,
  Get,
  ORDER,
  formattedDateForSubscription,
  useCombinedErrorObs,
  useConstant,
  useObservable,
  useStaticSubscription,
  useWsPaginationLimiter,
  wsStitchWith,
  wsWaitForAllPages,
  type ExecutionReport,
  type Order,
  type RequestStream,
} from '@talos/kyoko';

import { ORDER_SUB_THROTTLE } from '../containers/Blotters/Orders/tokens';
import { useOpenCustomerOrders } from '../containers/Blotters/Orders/useOpenCustomerOrders';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useAuxiliaryOrderData } from './AuxiliaryOrderDataProvider';
import { OPEN_ORDER_STATUSES } from './constants';
import type { OrdersContextProps } from './orders.types';
import { ordersProviderMergeOrderUpdate } from './ordersProviderMergeOrderUpdate';
import { OrgConfigurationKey, useOrgConfiguration } from './OrgConfigurationProvider';

const RECENT_ORDERS_THROTTLE_MS = 1_000;
const RECENT_ORDERS_RECORDS_MAX = 10_000;

const Orders = createContext<OrdersContextProps | undefined>(undefined);
Orders.displayName = 'OrdersContext';

export const useOrders = () => {
  const context = useContext(Orders);
  if (context === undefined) {
    throw new Error('Missing OrderContext.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
};

export const OrdersProvider = memo(function OrdersProvider(props: React.PropsWithChildren<unknown>) {
  const { enableOrderAuxData } = useFeatureFlag();
  // More then Group is returned by this RestEndpoint but it is the only property caller of this crappy function need
  const historicalOrders = useCallback((endpoint: string) => {
    const queryString = new URLSearchParams();
    queryString.append('offset', '0');
    queryString.append('limit', '100');
    queryString.append('orderBy', '-SubmitTime');
    return Get<{ data: Pick<ExecutionReport, 'Group'>[] }>(
      endpoint,
      `/organization/blotters/OMSOrders?${queryString.toString()}`
    );
  }, []) satisfies OrdersContextProps['historicalOrders'];

  // Recent Orders (Last 2 days)
  // TODO why are we doing this? why can't we just subscribe to open orders only? might be legacy from when this was used for the
  // recent orders blotter but now it's only being used for the open orders blotter.
  // TODO should this include the loadAll flag? I think if we don't load all of the orders
  const recentReq = useMemo(() => {
    const r: RequestStream = {
      name: ORDER,
      tag: 'RECENT_ORDERS',
      StartDate: formattedDateForSubscription(Sugar.Date.create('2 days ago')),
      sort_by: '-SubmitTime',
      HideApiCalls: true,
      Throttle: ORDER_SUB_THROTTLE,
      CoalesceExecs: [ExecTypeEnum.Restated, ExecTypeEnum.Trade],
    };
    if (enableOrderAuxData) {
      r.IncludeAuxiliaryData = true;
    }
    return r;
  }, [enableOrderAuxData]);
  const {
    data: recentSub,
    error: recentOrdersSubError,
    nextPage: recentSubNextPage,
  } = useStaticSubscription<Order>(
    recentReq,
    { loadAll: false } // set to false and control from the pagination limiter below
  );

  // Open Orders (All time)
  const openReq = useMemo(() => {
    const r: RequestStream = {
      name: ORDER,
      tag: 'OPEN_ORDERS',
      Statuses: OPEN_ORDER_STATUSES,
      sort_by: '-Timestamp',
      HideApiCalls: true,
      Throttle: ORDER_SUB_THROTTLE,
      CoalesceExecs: [ExecTypeEnum.Restated, ExecTypeEnum.Trade],
    };
    if (enableOrderAuxData) {
      r.IncludeAuxiliaryData = true;
    }
    return r;
  }, [enableOrderAuxData]);
  const { data: openSub, error: openOrdersSubError } = useStaticSubscription<Order>(
    openReq,
    { loadAll: true } // loadAll defaults to true. We're just being explicit here for clarity.
  );

  const recentOrdersError = useCombinedErrorObs([recentOrdersSubError, openOrdersSubError]);

  const { getConfig } = useOrgConfiguration();
  const limit = useConstant(getConfig(OrgConfigurationKey.RecentOrderRowsMax, RECENT_ORDERS_RECORDS_MAX));
  const recentSubPaginationLimiter = useWsPaginationLimiter<Order>({
    startingLimit: limit,
    nextPage: recentSubNextPage,
  });
  const pageLimitedRecentSub = useMemo(
    () => recentSub.pipe(recentSubPaginationLimiter.wsPaginationLimiterInstance),
    [recentSub, recentSubPaginationLimiter.wsPaginationLimiterInstance]
  );

  const openCustomerOrdersObs = useOpenCustomerOrders();

  // Hedge order status update stitching onto orders
  const { hedgeOrderStatusObs, orderAnnotationsObs } = useAuxiliaryOrderData();

  const enrichedOpenSub = useMemo(() => {
    return openSub.pipe(
      wsWaitForAllPages({ getUniqueKey: order => order.OrderID }),
      wsStitchWith({
        secondarySource: hedgeOrderStatusObs,
        getPrimaryTypeKey: order => order.OrderID,
        getSecondaryTypeKey: hos => hos.InitiatingOrderID,
        stitch: (order, hos) => {
          if (hos) {
            order.enrichWithHedgeOrderStatus(hos);
          }
          return order;
        },
      }),
      wsStitchWith({
        secondarySource: orderAnnotationsObs,
        getPrimaryTypeKey: order => order.OrderID,
        getSecondaryTypeKey: annotation => annotation.OrderID,
        stitch: (order, annotation) => {
          if (annotation) {
            order.enrichWithOrderAnnotation(annotation);
          }
          return order;
        },
      })
    );
  }, [hedgeOrderStatusObs, openSub, orderAnnotationsObs]);

  const enrichedRecentSub = useMemo(() => {
    return pageLimitedRecentSub.pipe(
      wsStitchWith({
        secondarySource: hedgeOrderStatusObs,
        getPrimaryTypeKey: order => order.OrderID,
        getSecondaryTypeKey: hos => hos.InitiatingOrderID,
        stitch: (order, hos) => {
          if (hos) {
            order.enrichWithHedgeOrderStatus(hos);
          }
          return order;
        },
      }),
      wsStitchWith({
        secondarySource: orderAnnotationsObs,
        getPrimaryTypeKey: order => order.OrderID,
        getSecondaryTypeKey: annotation => annotation.OrderID,
        stitch: (order, annotation) => {
          if (annotation) {
            order.enrichWithOrderAnnotation(annotation);
          }
          return order;
        },
      })
    );
  }, [hedgeOrderStatusObs, orderAnnotationsObs, pageLimitedRecentSub]);

  // Make sure we're always storing the data received from the two subscriptions above,
  // even if no one is listening on e.g. the `recentOrders` observable.
  const recentAndOpenOrdersObs = useConstant(new ReplaySubject<Map<string, Order>>(1));
  useEffect(() => {
    const subscription = merge(enrichedRecentSub, enrichedOpenSub)
      .pipe(scan(ordersProviderMergeOrderUpdate, new Map<string, Order>()))
      .subscribe(orders => recentAndOpenOrdersObs.next(orders));
    return () => {
      subscription.unsubscribe();
    };
  }, [enrichedOpenSub, enrichedRecentSub, recentAndOpenOrdersObs]);

  // Recent Orders (Combined)
  const recentOrders = useObservable(
    () =>
      combineLatest([recentAndOpenOrdersObs, openCustomerOrdersObs]).pipe(
        map(([orders, openCustomerOrders]) =>
          Array.from(orders.values()).map((order: Order) => {
            if (order.ParentOrderID && openCustomerOrders.has(order.ParentOrderID)) {
              const customerOrder = openCustomerOrders.get(order.ParentOrderID);
              order.enrichOrderWithCustomerOrder(customerOrder);
            }
            return order;
          })
        ),
        throttleTime(RECENT_ORDERS_THROTTLE_MS, asyncScheduler, {
          leading: true,
          trailing: true,
        }),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      ),
    [recentAndOpenOrdersObs, openCustomerOrdersObs]
  );

  return (
    <Orders.Provider
      value={{
        recentOrders,
        recentOrdersError,
        historicalOrders,
      }}
    >
      {props.children}
    </Orders.Provider>
  );
});
